import { api } from "@/lib/apiClient";

export type MerosZustand = "oben" | "unten";

export interface MerosEintrag {
  id: number;
  zustand: MerosZustand;
  erfasst_am: string | null;
  notiz: string;
  erfasst_von: string;
}

export interface MerosAktuell {
  zustand: MerosZustand;
  seit: string | null;
  art: "gesetzt" | "klapp";
  notiz: string;
  erfasst_von: string;
}

export interface MerosVerlaufSegment {
  von: string;
  bis: string | null;
  zustand: MerosZustand;
  art: "gesetzt" | "klapp";
  quelle_id: number;
  notiz: string;
}

export interface MerosKlappTermin {
  id: number;
  klapp_am: string;
  notiz: string;
  erfasst_von: string;
  zustand_davor: MerosZustand | null;
  zustand_danach: MerosZustand | null;
  color?: string | null;
}

export interface MerosFarben {
  oben: string;
  unten: string;
}

export interface MerosPlanBar {
  startIdx: number;
  span: number;
  zustand: MerosZustand;
  art: "gesetzt" | "klapp";
  notiz: string;
  von: string;
}

export interface MerosBedarf {
  mail_id: number;
  event_title: string;
  event_date: string;
  venue: string;
  rp_nummer: string;
  bereich: string;
  ort: string;
  kategorie: string;
  datum: string;
  von: string | null;
  bis: string | null;
  aufgabe: string;
  notiz: string;
  zustand: MerosZustand | null;
}

export interface MerosUebersicht {
  aktuell: MerosAktuell | null;
  historie: MerosEintrag[];
  bedarf: MerosBedarf[];
  verlauf: MerosVerlaufSegment[];
  klapp_termine: MerosKlappTermin[];
  farben?: MerosFarben;
}

const MEROS_RE = /merost/i;
const UNTEN_RE = /\bunten\b|runter/i;
const OBEN_RE = /\boben\b|\bhochklapp|\bhoch\b|\brauf\b/i;

function richtung(text: string): MerosZustand | null {
  const unten = UNTEN_RE.exec(text);
  const oben = OBEN_RE.exec(text);
  if (unten && !oben) return "unten";
  if (oben && !unten) return "oben";
  if (unten && oben) return unten.index <= oben.index ? "unten" : "oben";
  return null;
}

/** Dieselbe Lesart wie in der API: Aufgabe zuerst, Notiz nur ohne Richtung in der Aufgabe. */
export function merosZustandAusText(
  aufgabe?: string | null,
  notiz?: string | null,
): MerosZustand | null {
  const task = (aufgabe || "").trim();
  if (MEROS_RE.test(task)) {
    const ausAufgabe = richtung(task);
    if (ausAufgabe) return ausAufgabe;
  }
  const text = `${aufgabe || ""} ${notiz || ""}`.trim();
  if (!text || !MEROS_RE.test(text)) return null;
  return richtung(text);
}

export function einsatzMerosZustand(einsatz: {
  aufgabe?: string | null;
  notiz?: string | null;
  meros_zustand?: string | null;
}): MerosZustand | null {
  if (einsatz.meros_zustand === "oben" || einsatz.meros_zustand === "unten") {
    return einsatz.meros_zustand;
  }
  return merosZustandAusText(einsatz.aufgabe, einsatz.notiz);
}

export function merosZustandLabel(zustand: MerosZustand | null | undefined): string {
  if (zustand === "oben") return "OBEN";
  if (zustand === "unten") return "UNTEN";
  return "unklar";
}

export function planDayIso(day: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

export function heuteIso(now = new Date()): string {
  return planDayIso(now);
}

/** Tage, an denen in der Meros-Zeile schon ein Einsatz liegt. */
export function merosOccupiedDays(
  assignments: Array<{ date_from: string; date_to?: string | null }>,
): Set<string> {
  const occupied = new Set<string>();
  for (const assignment of assignments) {
    const startIso = (assignment.date_from || "").slice(0, 10);
    const endIso = (assignment.date_to || assignment.date_from || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startIso) || !/^\d{4}-\d{2}-\d{2}$/.test(endIso)) continue;
    const startParts = startIso.split("-").map(Number);
    const endParts = endIso.split("-").map(Number);
    const startYear = startParts[0];
    const startMonth = startParts[1];
    const startDay = startParts[2];
    const endYear = endParts[0];
    const endMonth = endParts[1];
    const endDay = endParts[2];
    if (
      startYear == null ||
      startMonth == null ||
      startDay == null ||
      endYear == null ||
      endMonth == null ||
      endDay == null
    ) {
      continue;
    }
    const cursor = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || end < cursor) continue;
    for (let guard = 0; guard < 800 && cursor <= end; guard += 1) {
      occupied.add(planDayIso(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return occupied;
}

export function bedarfAnstehend(
  item: { von: string | null; bis: string | null },
  heute = heuteIso(),
): boolean {
  const ende = item.bis || item.von;
  if (!ende) return true;
  return ende >= heute;
}

/**
 * Balken für die Meros-Zeile.
 * Am Klapp-Tag und an Tagen mit einem Einsatz bleibt der Zustand unsichtbar,
 * damit der nächste Zustand erst danach beginnt.
 */
export function merosPlanBars(
  segments: MerosVerlaufSegment[],
  days: Date[],
  occupiedDays?: ReadonlySet<string>,
): MerosPlanBar[] {
  const klappDays = new Set<string>();
  for (const segment of segments) {
    if (segment.art !== "klapp") continue;
    const von = new Date(segment.von);
    if (!Number.isNaN(von.getTime())) klappDays.add(planDayIso(von));
  }
  const bars: MerosPlanBar[] = [];
  days.forEach((day, index) => {
    const iso = planDayIso(day);
    if (klappDays.has(iso) || occupiedDays?.has(iso)) return;
    const start = new Date(day);
    start.setHours(0, 0, 0, 0);
    const end = new Date(day);
    end.setHours(23, 59, 59, 999);
    let segment: MerosVerlaufSegment | null = null;
    for (const item of segments) {
      const von = new Date(item.von).getTime();
      if (Number.isNaN(von) || von > end.getTime()) continue;
      if (item.bis) {
        const bis = new Date(item.bis).getTime();
        if (!Number.isNaN(bis) && bis <= start.getTime()) continue;
      }
      segment = item;
    }
    if (!segment) return;
    const previous = bars[bars.length - 1];
    if (previous && previous.zustand === segment.zustand && previous.startIdx + previous.span === index) {
      previous.span += 1;
      return;
    }
    bars.push({
      startIdx: index,
      span: 1,
      zustand: segment.zustand,
      art: segment.art,
      notiz: segment.notiz,
      von: segment.von,
    });
  });
  return bars;
}

export function getMerosUebersicht() {
  return api.get<MerosUebersicht>("/veranstaltungen/meros");
}

export function getMerosVerlauf() {
  return api.get<{ verlauf: MerosVerlaufSegment[]; farbe_oben?: string; farbe_unten?: string }>(
    "/montageplanung/meros-verlauf",
  );
}

export function legeMerosKlappTermin(payload: { klapp_am: string; notiz?: string; color?: string }) {
  return api.post<MerosUebersicht>("/veranstaltungen/meros/klapp", payload);
}

export function loescheMerosKlappTermin(id: number) {
  return api.del<MerosUebersicht>(`/veranstaltungen/meros/klapp/${id}`);
}

export function speichereMerosFarben(farben: MerosFarben) {
  return api.put<MerosUebersicht>("/veranstaltungen/meros/farben", {
    farbe_oben: farben.oben,
    farbe_unten: farben.unten,
  });
}

export function erfasseMerosZustand(payload: {
  zustand: MerosZustand;
  erfasst_am?: string;
  notiz?: string;
}) {
  return api.post<MerosUebersicht>("/veranstaltungen/meros", payload);
}
